// UI copy for the Revenue Plays commercial model. English is the source of
// truth and verbatim per PLAYS_PRICING_SPEC.md section 7 (do not paraphrase
// the headline block, always-on explainer, plan one-liners, Klaviyo
// objection, or the free gate / multi-store / founding disclosure strings:
// those are also used by the backend and must read identically everywhere).
// No em dashes. Never use "slot", "credits", "tokens", or "quota" client-side
// (use "monthly Plays" / "always-on Plays" instead).
//
// `es` mirrors `en` for now (PENDING: Spanish copy not localized yet), same
// convention as the old Billing/billingCopy.js.

export const playsTranslations = {
  en: {
    pricing: {
      headlineEyebrow: 'Ask anything. Explore without limits.',
      headlineBody:
        'Launching a Play counts once. Keeping it updated and monitored counts while it runs. Every Play includes unlimited campaigns.',
      alwaysOnExplainer:
        'Unlimited campaigns inside every Always-on Play. Sync it, message it and retarget it as many times as you need. We charge for keeping the strategy alive, not for the campaigns you run inside it.',
      klaviyoObjection:
        'Klaviyo sends the campaigns. UPSELLerate decides which revenue play to run, builds the right audience, keeps it updated and tells you what to do next.',
      coreOneLiner: 'Core helps you find and launch the right Revenue Plays.',
      proOneLiner: 'Pro helps you coordinate, prioritize and improve them continuously.',
      monthly: 'Monthly',
      selectPlan: 'Choose plan',
      currentPlan: 'Current plan',
      updatePacks: 'Update Play Packs',
      freePlan: 'Free plan',
      downgradeToFree: 'Downgrade to Free',
      processing: 'Redirecting to checkout...',
      addPack: 'Add a Play Pack',
      genericError: 'Could not start checkout. Please try again.',
      mostPopular: 'Most popular',
      perMonth: '/mo',
      oneTimeIncluded: (n) => `${n ?? 0} one-time Plays / month`,
      alwaysOnIncluded: (n) => `${n} always-on Play${n === 1 ? '' : 's'} included`,
      campaignsUnlimited: 'Unlimited campaigns per Play',
      playPackLabel: 'Play Pack',
      playPackPrice: (price) => `+$${price}/mo`,
      playPackCoreContents: 'Adds 2 always-on Plays and 2 one-time Plays / month',
      playPackProContents: 'Adds 2 always-on Plays and 3 one-time Plays / month (always-on up to 12; one-time unlimited)',
      playPackMax: (max) => `Up to ${max} Play Packs`,
      playPackUnlimited: 'Unlimited Play Packs',
      playPackNotAvailable: 'Not available on Free',
      detectedPlays: 'Unlimited Detected Opportunities to Play',
      resultsHistoryLimited: 'Limited results history',
      resultsHistoryFull: "Track each Play's revenue impact",
      basicCalendar: 'Basic calendar',
      assistedOnboarding: 'Assisted onboarding',
      selfServiceOnboarding: 'Self-service onboarding',
      multiUser: 'Multiple users',
      singleUser: '1 user',
      exportBlocked: 'CSV export / external sync: blocked',
      exportAllowed: 'CSV export / external sync: yes',
      freeGate: 'Launching Plays is available on Core. Your draft is saved and ready.',
      multiStore: 'Running multiple stores? Talk to us.',
      talkToUs: 'Talk to us',
      // Mandatory GMV footnote (verbatim; backend plays_model.GMV_PRICING_FOOTNOTE
      // is the source of truth via /billing/plans `pricing_footnote`; this local
      // fallback must stay identical). "Talk to us." renders as a contact link.
      gmvFootnote:
        'Pricing shown for stores up to $3M in annual revenue. Larger store? Your pricing adjusts to your size. Talk to us.',
      gmvCheckoutContinue: 'Continue to checkout',
    },
    meter: {
      build: (oneTimeUsed, oneTimeTotal, alwaysOnUsed, alwaysOnTotal) =>
        `This month: ${oneTimeUsed} of ${oneTimeTotal} one-time Plays used · ${alwaysOnUsed} of ${alwaysOnTotal} always-on Plays used`,
      // Compatibility only for API versions that do not yet expose retained
      // always-on consumption. This says "running", never "used".
      buildLegacy: (used, total, active) =>
        `This month: ${used} of ${total} one-time Plays used · ${active} always-on running`,
    },
    activation: {
      launchOnce: 'Launch once',
      keepUpdated: 'Keep updated automatically',
      launchOnceDesc: 'Built, delivered, done. Counts once against your monthly Plays.',
      keepUpdatedDesc: 'Audience refreshed and monitored automatically. Occupies one always-on Play while active.',
      confirm: 'Confirm',
      cancel: 'Cancel',
      launching: 'Launching...',
      title: 'Launch this Play',
      alwaysOnLimit: (active, total) =>
        `You have ${active} of ${total} always-on Plays running. Add a Play Pack or pause another Play.`,
      oneTimeLimit: 'You are out of one-time Plays for this cycle. Add a Play Pack or upgrade your plan.',
      freeBlocked: 'Launching Plays is available on Core. Your draft is saved and ready.',
      genericError: 'Could not launch this Play. Please try again.',
      addPack: 'Add a Play Pack',
      upgrade: 'Upgrade plan',
    },
    dashboard: {
      title: 'Impact',
      subtitle: 'Everything you have launched, and what it is doing for you.',
      empty: 'No Plays yet. Build one from a Detected Play or ask the chat to draft one.',
      status: {
        draft: 'Draft',
        active: 'Active',
        paused: 'Paused',
        archived: 'Archived',
      },
      typeOneTime: 'One-time',
      typeAlwaysOn: 'Always-on',
      campaignsRun: (n) => `${n} campaign${n === 1 ? '' : 's'} run`,
      correlatedRevenue: (value) => `${value} correlated revenue (30d)`,
      correlatedRevenueNote: 'Correlational, not causal. Attribution loop is not closed yet.',
      pause: 'Pause',
      archive: 'Archive',
      resume: 'Resume',
      overCapacityTitle: 'You are over your always-on capacity',
      overCapacityBody: (active, allowed) =>
        `${active} always-on Plays are running but your plan allows ${allowed}. They keep running; pause one or add a Play Pack before launching another.`,
    },
    founding: {
      disclosure:
        'Founding access: the Pro plan at $49 for 6 months. After that you pick your plan at standard pricing.',
      badge: 'Founding',
    },
    checkout: {
      success: 'Your subscription is now active.',
      error: 'Something went wrong confirming your subscription. Please try again.',
      reauth: 'Your Shopify connection expired. Please reconnect Shopify and try again.',
      retry: 'Try again',
      dismiss: 'Dismiss',
    },
  },
};

// Spanish mirrors English until localized (PENDING).
playsTranslations.es = playsTranslations.en;
